import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { FilterForm, Searchbox } from 'ui/components/form';
import { PagePlaceholder } from 'ui/partials';
import useModal, { useExternalModal } from 'ui/partials/Modal/useModal';
import { POSITION } from 'utils/constants';
import { Dropdown } from 'ui/components/nav';
import { OPTIONS_PROPS } from 'ui/components/nav/Dropdown/Dropdown';
import { Button } from 'ui/components/common';
import { ACCOUNT_USER, EmailTemplate } from 'utils/types';
import { dayjs } from 'utils/providers/Timezone';
import { cn, modifyParsedHTMLcontent } from 'utils/helpers';
import { useInView } from 'react-intersection-observer';
import { CgSpinner } from 'react-icons/cg';
import { Loading } from 'ui/partials/PagePlaceholder/PagePlaceholder';

import EmailTemplateModal from './EmailTemplateModal';
import useGetEmailTemplatesInfinite from '../hooks/useGetEmailTemplatesInfinite';

const FILTER_INIT = {
  sortBy: 'createdAt',
  orderBy: 'ASC',
  page: 1,
  perPage: 10,
  keyword: '',
};

const options: OPTIONS_PROPS[] = [
  {
    label: 'Mine',
    value: false,
  },
  {
    label: 'Everyone',
    value: true,
  },
];

const SelectEmailTemplateModal: FC<{
  onClick: (emailTemplate: EmailTemplate) => void;
  onCancel?: () => void;
  hideCreateButton?: boolean;
}> = ({ onClick: onSelect, hideCreateButton, onCancel }) => {
  const [query, setQuery] = useState({});
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    data: emailTemplatesScrollableData,
    isLoading,
    isInitialLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetEmailTemplatesInfinite({
    ...FILTER_INIT,
    ...query,
    include: ['accountUser'],
  });

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const handleFilterOnChange = (rest: { [key: string]: any }) => {
    setQuery(rest);
  };

  const { hideModal } = useModal();
  const { showModal: showExternalModal, hideModal: hideExternalModal } =
    useExternalModal();

  const handleShowCreateEmailTemplateModal = useCallback(() => {
    showExternalModal({
      content: <EmailTemplateModal closeModal={hideExternalModal} />,
      title: 'Create Email Template',
      position: POSITION.Top,
    });
  }, [hideExternalModal, showExternalModal]);

  const [selectedTemplate, setSelectedTemplate] = useState<
    EmailTemplate | undefined
  >(emailTemplatesScrollableData?.pages?.[0]?.data?.[0]);

  const hasTemplates = emailTemplatesScrollableData?.pages?.[0]?.data.length;

  return (
    <FilterForm initialValues={query} onChange={handleFilterOnChange}>
      {({ values, handleChange }) => (
        <div className="rounded-md bg-fill-tertiary">
          {isLoading ||
            (isInitialLoading && (
              <Loading description="Fetching saved templates" />
            ))}
          <div className="flex gap-2 text-sm">
            <div className=" px-4 py-5 space-y-[18px]" ref={containerRef}>
              <div className="space-y-4 border-b border-separator-secondary">
                <Searchbox
                  placeholder="Search field"
                  className="w-[290px]"
                  onChange={debounce(
                    (event: React.ChangeEvent<HTMLInputElement>) => {
                      handleChange('keyword', event.target.value);
                    },
                    300,
                  )}
                />
                <div className="font-semibold text-sm leading-[22px] tracking-wider pb-3">
                  Saved Templates
                </div>
              </div>
              {!isLoading && (
                <div className="scrollbar-thin overflow-y-auto max-h-[560px] p-[1px] space-y-[6px]">
                  {emailTemplatesScrollableData?.pages
                    ?.flatMap((d) => d.data)
                    .map((emailTemplate) => (
                      <ListItem
                        onSelect={onSelect}
                        selected={selectedTemplate?._id === emailTemplate._id}
                        key={emailTemplate._id}
                        onClick={() => setSelectedTemplate(emailTemplate)}
                        emailTemplate={emailTemplate}
                      />
                    ))}
                  {(containerRef?.current?.scrollHeight ?? 0) >=
                    (containerRef?.current?.clientHeight ?? 0) && (
                    <div ref={ref} className="flex items-center justify-center">
                      {hasNextPage ? (
                        <div className="flex flex-col items-center">
                          <CgSpinner className="w-5 h-5 animate-spin text-primary-base" />
                          <div className="text-sm text-content">
                            Fetching templates
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col flex-1 px-4 py-5 text-sm bg-fill-secondary">
              {/* Filter */}
              <div className="flex items-center justify-between">
                <Dropdown
                  placeholder="Ownership"
                  options={options}
                  defaultValue={options[0].value}
                  value={values.type}
                  onChange={(opt) => handleChange('isShared', opt.value)}
                />
                {!hideCreateButton && (
                  <Button
                    onClick={handleShowCreateEmailTemplateModal}
                    label="ADD EMAIL TEMPLATE"
                  />
                )}
              </div>
              <div className="space-y-[14px] flex-1">
                {selectedTemplate ? (
                  <>
                    {/* Details */}
                    <div
                      className={cn(
                        'flex justify-between pt-4 pb-[18px] border-b border-b-separator-secondary',
                      )}
                    >
                      <div className="text-xs text-content">
                        Created:{' '}
                        {dayjs().to(dayjs(selectedTemplate?.createdAt))}
                      </div>
                      <div className="text-xs text-content">
                        Updated:{' '}
                        {dayjs().to(dayjs(selectedTemplate?.updatedAt))}
                      </div>
                    </div>
                    {/* Preview */}
                    <div className="min-h-[400px] space-y-5 flex flex-col">
                      <div className="flex flex-col gap-2">
                        <div className="font-semibold">Subject:</div>
                        <div className="h-full p-2 border rounded-md border-separator-secondary">
                          {selectedTemplate?.subject}
                        </div>
                      </div>

                      <div className="flex flex-col flex-1 gap-2">
                        <div className="font-semibold">Body:</div>
                        <div
                          className={cn(
                            'bg-fill-tertiary',
                            'text-sm',
                            'border',
                            'border-separator-secondary',
                            'rounded-md',
                            'p-2',
                            'flex-1',
                            'min-h-[450px]',
                            'max-h-[450px]',
                            'overflow-auto',
                            'scrollbar-thin',
                          )}
                          dangerouslySetInnerHTML={{
                            __html: modifyParsedHTMLcontent(
                              selectedTemplate?.body ?? '',
                            ),
                          }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="grid flex-1 h-full place-items-center">
                    {!hasTemplates ? (
                      <PagePlaceholder
                        className="py-5 h-[400px]"
                        iconType="no-data"
                        title="No record yet"
                        backButtonText="ADD EMAIL TEMPLATE"
                        onClick={handleShowCreateEmailTemplateModal}
                        description="There seems to be no records available to show."
                      />
                    ) : (
                      'Choose a template to display its preview'
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className={cn(
              'flex justify-end gap-3',
              'px-[22px] py-4',
              'border-t-[0.5px] border-separator-primary',
            )}
          >
            <Button
              onClick={onCancel ?? hideModal}
              label="CANCEL"
              size="sm"
              intent="tertiary"
            />
            <Button
              onClick={() => {
                if (selectedTemplate) {
                  onSelect(selectedTemplate);
                }
              }}
              label="USE TEMPLATE"
              size="sm"
              disabled={!selectedTemplate}
            />
          </div>
        </div>
      )}
    </FilterForm>
  );
};

export default SelectEmailTemplateModal;

type ListItemProps = {
  onClick: () => void;
  onSelect: (emailTemplate: EmailTemplate) => void;
  emailTemplate: EmailTemplate;
  selected: boolean;
};

const ListItem: FC<ListItemProps> = ({
  onClick,
  onSelect,
  emailTemplate,
  selected,
}) => {
  return (
    <button
      tabIndex={0}
      role="button"
      onKeyDown={(e) => {
        if (e.code === 'Enter') {
          onSelect(emailTemplate);
        }
      }}
      onDoubleClick={() => onSelect(emailTemplate)}
      onClick={onClick}
      className={cn(
        'group',
        'w-full',
        'text-left',
        'flex justify-between px-3 py-2 gap-2 w-[260px]',
        selected
          ? 'bg-[#E9FAF0] rounded-md outline outline-1 outline-primary-base'
          : 'bg-fill-tertiary',
        'hover:bg-[#E9FAF0] hover:rounded-md hover:outline outline-1 hover:outline-primary-base',
      )}
    >
      <div>
        {/* template name */}
        <div className="font-semibold">{emailTemplate?.name}</div>
        {/* author name */}
        <div
          className={cn(
            'text-xs',
            selected ? 'text-secondary-dark' : 'text-content',
          )}
        >
          {(emailTemplate?.accountUserId as ACCOUNT_USER).fullName}
        </div>
      </div>
    </button>
  );
};
